import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
          background: 'var(--color-bg-primary, #fef2f2)',
          color: 'var(--color-text-primary, #991b1b)',
        }}>
          <h1 style={{ fontSize: 'var(--fs-2xl)', marginBottom: '0.75rem' }}>
            This screen didn't load right
          </h1>
          <p style={{ fontSize: 'var(--fs-base)', color: 'var(--color-text-secondary, var(--color-text-secondary))', marginBottom: '1.5rem', maxWidth: '28rem' }}>
            Your data is safe on this device. Reload the page to try again. If it keeps happening, let us know — there's a contact link in the menu.
          </p>
          {this.state.error && (
            <pre style={{
              fontSize: 'var(--fs-xs)',
              background: 'var(--color-bg-surface, #fef2f2)',
              color: 'var(--color-text-error, #991b1b)',
              padding: '1rem',
              borderRadius: 'var(--radius-sm)',
              maxWidth: '40rem',
              overflow: 'auto',
              marginBottom: '1.5rem',
              textAlign: 'left',
            }}>
              {this.state.error.message}
              {'\n\n'}
              {this.state.error.stack}
            </pre>
          )}
          {/* eslint-disable-next-line cw/no-inline-button-style -- ErrorBoundary Reload — fallback-only CTA when React tree has thrown; design-system access may itself be broken */}
          <button
            onClick={this.handleReload}
            style={{
              fontSize: 'var(--fs-sm)',
              padding: '0.625rem 1.5rem',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'var(--color-accent, var(--color-accent))',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
